/**
 * OTO Order Validation Utilities
 * Provides sanity checks and reasonable defaults for stop loss and take profit orders
 */

import { getTickSize } from "./priceUtils.js";

/**
 * Calculate reasonable stop loss and take profit distances
 * @param {string} symbol - Trading symbol
 * @param {number} mainPrice - Main order price
 * @param {string} side - Order side ('buy' or 'sell')
 * @returns {Object} { stopDistance, profitDistance, stopPrice, profitPrice }
 */
export const calculateReasonableOTODistances = (symbol, mainPrice, side) => {
  // Get the actual tick size for the symbol
  const tickSize = getTickSize(symbol);

  // Define reasonable ranges in NUMBER OF TICKS
  const minStopTicks = 2; // Minimum 5 ticks from main price
  const maxStopTicks = 5000; // Maximum 200 ticks from main price
  const defaultStopTicks = 5; // Default 10 ticks from main price

  const minProfitTicks = 2; // Minimum 5 ticks from main price
  const maxProfitTicks = 5000; // Maximum 400 ticks from main price
  const defaultProfitTicks = 5; // Default 10 ticks from main price

  const isBuy = side === "buy";

  // Calculate default distances using actual tick size
  const stopDistance = isBuy
    ? -defaultStopTicks * tickSize
    : defaultStopTicks * tickSize;
  const profitDistance = isBuy
    ? defaultProfitTicks * tickSize
    : -defaultProfitTicks * tickSize;

  return {
    stopDistance,
    profitDistance,
    stopPrice: mainPrice + stopDistance,
    profitPrice: mainPrice + profitDistance,
    limits: {
      // For buy orders: stop loss should be below main (negative distances)
      // minStopDistance = closest stop can be (least negative), maxStopDistance = farthest stop can be (most negative)
      minStopDistance: isBuy
        ? -minStopTicks * tickSize
        : minStopTicks * tickSize,
      maxStopDistance: isBuy
        ? -maxStopTicks * tickSize
        : maxStopTicks * tickSize,
      // For buy orders: take profit should be above main (positive distances)
      // minProfitDistance = closest profit can be (least positive), maxProfitDistance = farthest profit can be (most positive)
      minProfitDistance: isBuy
        ? minProfitTicks * tickSize
        : -minProfitTicks * tickSize,
      maxProfitDistance: isBuy
        ? maxProfitTicks * tickSize
        : -maxProfitTicks * tickSize,
    },
  };
};

/**
 * Calculate reasonable stop loss and take profit distances (rewritten version)
 * @param {string} symbol - Trading symbol
 * @param {number} mainPrice - Main order price
 * @param {string} side - Order side ('buy' or 'sell')
 * @returns {Object} { stopDistance, profitDistance, stopPrice, profitPrice, limits }
 */
export const calculateReasonableOTODistances2 = (symbol, mainPrice, side) => {
  // Get the actual tick size for the symbol
  const tickSize = getTickSize(symbol);

  // Define reasonable ranges in NUMBER OF TICKS
  const minStopTicks = 1; // Minimum 0 ticks from main price (allow same price stops)
  const maxStopTicks = 5000; // Maximum 400 ticks from main price
  const defaultStopTicks = 5; // Default 5 ticks from main price

  const minProfitTicks = 1; // Minimum 0 ticks from main price (allow same price profits)
  const maxProfitTicks = 5000; // Maximum 400 ticks from main price
  const defaultProfitTicks = 5; // Default 5 ticks from main price

  const isBuy = side === "buy";

  // Calculate distances in PRICE POINTS (ticks * tickSize)
  let stopDistance, profitDistance;
  let minStopDistance, maxStopDistance, minProfitDistance, maxProfitDistance;

  if (isBuy) {
    // BUY ORDER: stop below main (negative), profit above main (positive)
    stopDistance = -defaultStopTicks * tickSize; // e.g., -10 * 5 = -50
    profitDistance = defaultProfitTicks * tickSize; // e.g., 10 * 5 = 50

    // Stop limits (negative values, more negative = farther from main)
    minStopDistance = -minStopTicks * tickSize; // e.g., -5 * 5 = -25 (closest)
    maxStopDistance = -maxStopTicks * tickSize; // e.g., -200 * 5 = -1000 (farthest)

    // Profit limits (positive values, more positive = farther from main)
    minProfitDistance = minProfitTicks * tickSize; // e.g., 5 * 5 = 25 (closest)
    maxProfitDistance = maxProfitTicks * tickSize; // e.g., 400 * 5 = 2000 (farthest)
  } else {
    // SELL ORDER: stop above main (positive), profit below main (negative)
    stopDistance = defaultStopTicks * tickSize; // e.g., 10 * 5 = 50
    profitDistance = -defaultProfitTicks * tickSize; // e.g., -10 * 5 = -50

    // Stop limits (positive values, more positive = farther from main)
    minStopDistance = minStopTicks * tickSize; // e.g., 5 * 5 = 25 (closest)
    maxStopDistance = maxStopTicks * tickSize; // e.g., 200 * 5 = 1000 (farthest)

    // Profit limits (negative values, more negative = farther from main)
    minProfitDistance = -minProfitTicks * tickSize; // e.g., -5 * 5 = -25 (closest)
    maxProfitDistance = -maxProfitTicks * tickSize; // e.g., -400 * 5 = -2000 (farthest)
  }

  return {
    stopDistance,
    profitDistance,
    stopPrice: mainPrice + stopDistance,
    profitPrice: mainPrice + profitDistance,
    limits: {
      minStopDistance,
      maxStopDistance,
      minProfitDistance,
      maxProfitDistance,
    },
    // Debug info
    debug: {
      symbol,
      tickSize,
      side,
      tickCounts: {
        minStopTicks,
        maxStopTicks,
        defaultStopTicks,
        minProfitTicks,
        maxProfitTicks,
        defaultProfitTicks,
      },
    },
  };
};

/**
 * Validate and clamp OTO price to reasonable range
 * @param {number} otoPrice - Proposed OTO price
 * @param {number} mainPrice - Main order price
 * @param {string} side - Main order side ('buy' or 'sell')
 * @param {string} otoType - OTO type ('stop' or 'profit')
 * @param {string} symbol - Trading symbol for tick size calculation
 * @returns {Object} { price: clampedPrice, clamped: boolean, reason?: string }
 */
export const validateOTOPrice = (
  otoPrice,
  mainPrice,
  side,
  otoType,
  symbol = "",
) => {
  const { limits } = calculateReasonableOTODistances2(symbol, mainPrice, side);
  const distance = otoPrice - mainPrice;
  const isStop = otoType === "stop";
  const isBuy = side === "buy";

  let clampedPrice = otoPrice;
  let clamped = false;
  let reason = null;

  if (isStop) {
    // Validate stop loss
    if (isBuy) {
      // Buy stop should be below main (negative distance)
      // minStopDistance is closest (least negative), maxStopDistance is farthest (most negative)
      if (distance > limits.minStopDistance) {
        clampedPrice = mainPrice + limits.minStopDistance;
        clamped = true;
        reason = "Stop loss too close to main price";
      } else if (distance < limits.maxStopDistance) {
        clampedPrice = mainPrice + limits.maxStopDistance;
        clamped = true;
        reason = "Stop loss too far from main price";
      }
    } else {
      // Sell stop should be above main (positive distance)
      // minStopDistance is closest (least positive), maxStopDistance is farthest (most positive)
      if (distance < limits.minStopDistance) {
        clampedPrice = mainPrice + limits.minStopDistance;
        clamped = true;
        reason = "Stop loss too close to main price";
      } else if (distance > limits.maxStopDistance) {
        clampedPrice = mainPrice + limits.maxStopDistance;
        clamped = true;
        reason = "Stop loss too far from main price";
      }
    }
  } else {
    // Validate take profit
    if (isBuy) {
      // Buy profit should be above main (positive distance)
      // minProfitDistance is closest (least positive), maxProfitDistance is farthest (most positive)
      if (distance < limits.minProfitDistance) {
        clampedPrice = mainPrice + limits.minProfitDistance;
        clamped = true;
        reason = "Take profit too close to main price";
      } else if (distance > limits.maxProfitDistance) {
        clampedPrice = mainPrice + limits.maxProfitDistance;
        clamped = true;
        reason = "Take profit too far from main price";
      }
    } else {
      // Sell profit should be below main (negative distance)
      // minProfitDistance is closest (least negative), maxProfitDistance is farthest (most negative)
      if (distance > limits.minProfitDistance) {
        clampedPrice = mainPrice + limits.minProfitDistance;
        clamped = true;
        reason = "Take profit too close to main price";
      } else if (distance < limits.maxProfitDistance) {
        clampedPrice = mainPrice + limits.maxProfitDistance;
        clamped = true;
        reason = "Take profit too far from main price";
      }
    }
  }

  return { price: clampedPrice, clamped, reason };
};

/**
 * Check if OTO configuration makes economic sense
 * @param {number} mainPrice - Main order price
 * @param {number} stopPrice - Stop loss price
 * @param {number} profitPrice - Take profit price
 * @param {string} side - Order side ('buy' or 'sell')
 * @returns {Object} { valid: boolean, issues: string[] }
 */
export const validateOTOConfiguration = (
  mainPrice,
  stopPrice,
  profitPrice,
  side,
) => {
  const issues = [];
  const isBuy = side === "buy";

  if (isBuy) {
    // For buy orders: stop < main < profit
    if (stopPrice >= mainPrice) {
      issues.push("Stop loss must be below main price for buy orders");
    }
    if (profitPrice <= mainPrice) {
      issues.push("Take profit must be above main price for buy orders");
    }
  } else {
    // For sell orders: profit < main < stop
    if (stopPrice <= mainPrice) {
      issues.push("Stop loss must be above main price for sell orders");
    }
    if (profitPrice >= mainPrice) {
      issues.push("Take profit must be below main price for sell orders");
    }
  }

  // Check if risk/reward ratio is reasonable (optional warning)
  const riskDistance = Math.abs(stopPrice - mainPrice);
  const rewardDistance = Math.abs(profitPrice - mainPrice);
  const riskRewardRatio = rewardDistance / riskDistance;

  if (riskRewardRatio < 0.5) {
    issues.push(
      "Risk/reward ratio is very poor (risk is much higher than potential reward)",
    );
  }

  return {
    valid: issues.length === 0,
    issues,
    riskRewardRatio,
  };
};
